import { type RefObject, useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';
import debounce from 'lodash/debounce';

import { type ScrollDirection } from './types';

const DEBOUNCE_DURATION = 50;
const MIN_SCROLL_OFFSET = 0;

interface UseScrollDirectionParams {
	onScroll: (direction: ScrollDirection) => void;
	ref: RefObject<HTMLDivElement>;
}

export function useScrollDirection(params: UseScrollDirectionParams): void {
	const { ref, onScroll } = params;
	const lastScrollY = useRef(0);

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		const onScrollDebounced = debounce(() => {
			const scrollY = ref.current!.scrollTop;
			const direction: ScrollDirection = scrollY > lastScrollY.current ? 'down' : 'up';

			if (
				scrollY - lastScrollY.current > MIN_SCROLL_OFFSET ||
				scrollY - lastScrollY.current < -1 * MIN_SCROLL_OFFSET
			) {
				// Call the callback directly in the event handler to avoid a chain-of-computation
				// anti-pattern (effect → state → effect).
				onScroll(direction);
			}

			lastScrollY.current = scrollY > 0 ? scrollY : 0;
		}, DEBOUNCE_DURATION);

		return bind(ref.current, {
			type: 'scroll',
			listener: onScrollDebounced,
			options: {
				capture: true,
			},
		});
		// Only depend on ref and onScrollCallback — no need to re-subscribe when scrollDirection changes.
	}, [ref, onScroll]);
}
